/* Our Walks — dotted world-map renderer.
 *
 * Draws the brand's dotted world map onto a <canvas> from the baked land mask
 * (tyt-walks-landmask.js). No runtime data fetch, deterministic. Pins are not
 * drawn here — they are static DOM placed by the Liquid section using the same
 * equirectangular projection, so they always sit on the right spot.
 *
 * Markup contract (see sections/tyt-walks-map.liquid):
 *   <div class="tyt-walks-map" data-tyt-walks-map>
 *     <canvas data-tyt-walks-map-canvas></canvas>
 *     ...absolutely-positioned pins...
 *   </div>
 *
 * The wrapper sizes the map (CSS height, or aspect-ratio on phones); this
 * script fills it with dots, at a spacing that scales with the width — see
 * gapFor() below.
 */
(function () {
  'use strict';

  var COLORS = {
    ocean: '#E7EDEC',
    land: 'rgba(122,90,31,.12)',
    dot: 'rgba(57,57,57,.46)',
    grid: 'rgba(57,57,57,.06)'
  };
  /* ── Dot spacing scales with the map's width (2026-09-04) ──
   *
   * This used to be a flat `GAP = 6` whatever the size of the map, and that is
   * what made the phone map stop looking like a map. The gap was tuned against
   * a desktop-width box: at ~1142px that is 190 dots across, which reads well
   * against a 384 x 160 land mask. The same constant on a 370px phone card
   * gives 61 dots across — 16% of the detail that exists — so Africa came out
   * a blob and the Mediterranean vanished entirely.
   *
   * The rule now is "hold the detail, not the number": aim for the same
   * DOTS_ACROSS at any width. Two clamps make that safe.
   *   - GAP_MAX 6 means the map is NEVER SPARSER THAN IT IS TODAY, and desktop
   *     comes out at exactly 6 (1142/190 = 6.01, clamped back to 6), so the
   *     desktop rendering is unchanged rather than merely similar.
   *   - GAP_MIN 3 is a floor, and it is a real one, not a round number. The
   *     canvas is drawn at a device pixel ratio capped at 2 (below), so a
   *     radius under about 0.6 CSS px lands on less than one device pixel and
   *     the map turns into a grey haze instead of dots. 3 is also where Maud
   *     drew the line on 2026-09-04: finer than that and the continents read
   *     as solid shapes rather than as the brand's dotted map.
   *
   * DOT_R is derived, never set on its own — it holds the original 1.3/6
   * radius-to-gap ratio, so the dots keep their proportions. Set it
   * independently and at a small gap the dots merge into a solid mass.
   */
  var GAP_MAX = 6;          // desktop spacing, and the sparsest allowed
  var GAP_MIN = 3;          // finest that still reads as dots at dpr 2
  var DOTS_ACROSS = 190;    // what a desktop-width map has always had
  var DOT_RATIO = 1.3 / 6;  // the original radius:gap proportion

  function gapFor(width) {
    return Math.min(GAP_MAX, Math.max(GAP_MIN, width / DOTS_ACROSS));
  }

  // Decode the base64 mask into a Uint8Array once, lazily.
  var MASK = null;
  function getMask() {
    if (MASK) return MASK;
    var src = window.TYT_WALKS_LANDMASK;
    if (!src) return null;
    var bin = atob(src.bits);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    MASK = {
      cols: src.cols,
      rows: src.rows,
      b: src.bounds,
      bytes: bytes
    };
    return MASK;
  }

  function landAt(m, lon, lat) {
    var b = m.b;
    var col = Math.floor(((lon - b.lonMin) / (b.lonMax - b.lonMin)) * m.cols);
    var row = Math.floor(((b.latMax - lat) / (b.latMax - b.latMin)) * m.rows);
    if (col < 0 || col >= m.cols || row < 0 || row >= m.rows) return false;
    var bit = row * m.cols + col;
    return (m.bytes[bit >> 3] >> (7 - (bit & 7))) & 1;
  }

  function draw(wrap, canvas) {
    var m = getMask();
    if (!m) return;
    var w = wrap.clientWidth, h = wrap.clientHeight;
    if (!w || !h) return;
    var GAP = gapFor(w);
    var DOT_R = GAP * DOT_RATIO;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    var ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    var b = m.b;
    var lonSpan = b.lonMax - b.lonMin, latSpan = b.latMax - b.latMin;
    var PX = function (lon) { return ((lon - b.lonMin) / lonSpan) * w; };
    var PY = function (lat) { return ((b.latMax - lat) / latSpan) * h; };
    var lonAt = function (px) { return b.lonMin + (px / w) * lonSpan; };
    var latAtY = function (py) { return b.latMax - (py / h) * latSpan; };

    // graticule
    var step = lonSpan > 120 ? 30 : 10;
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    var lon, lat, x, y;
    for (lon = Math.ceil(b.lonMin / step) * step; lon <= b.lonMax; lon += step) {
      x = PX(lon); ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (lat = Math.ceil(b.latMin / step) * step; lat <= b.latMax; lat += step) {
      y = PY(lat); ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }

    // faint land-tint squares
    ctx.fillStyle = COLORS.land;
    for (var py = 0; py < h; py += GAP) {
      for (var px = 0; px < w; px += GAP) {
        if (landAt(m, lonAt(px + GAP / 2), latAtY(py + GAP / 2))) {
          ctx.fillRect(px, py, GAP, GAP);
        }
      }
    }
    // crisp dots
    ctx.fillStyle = COLORS.dot;
    for (var py2 = GAP / 2; py2 < h; py2 += GAP) {
      for (var px2 = GAP / 2; px2 < w; px2 += GAP) {
        if (landAt(m, lonAt(px2), latAtY(py2))) {
          ctx.beginPath();
          ctx.arc(px2, py2, DOT_R, 0, 6.2832);
          ctx.fill();
        }
      }
    }
  }

  function init(wrap) {
    var canvas = wrap.querySelector('[data-tyt-walks-map-canvas]');
    if (!canvas) return;
    // ocean background sits on the wrapper via CSS; the canvas draws over it.
    var render = function () { draw(wrap, canvas); };
    render(); // synchronous first paint after layout
    // a few retries cover late font/layout settles and backgrounded-tab throttling
    [60, 200, 600].forEach(function (t) { setTimeout(render, t); });
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(render);
      ro.observe(wrap);
    } else {
      window.addEventListener('resize', render);
    }
  }

  function boot() {
    var wraps = document.querySelectorAll('[data-tyt-walks-map]');
    for (var i = 0; i < wraps.length; i++) init(wraps[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the Shopify theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (e) {
    var wraps = e.target.querySelectorAll('[data-tyt-walks-map]');
    for (var i = 0; i < wraps.length; i++) init(wraps[i]);
  });
})();
